import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Box, CircularProgress } from '@mui/material'
import parse from 'html-react-parser'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getCountryCode } from 'src/business/services'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import LocationService from 'src/services/APIs/Geolocation/GetGeolocationScript'
import { CustomCheckBox } from '../../../consumer/components/helperComponents'
import MentorHeader from '../MentorHeader'
import {
  AgreeTermsButton,
  HeadingFieldDiv,
  HeadingFields,
  LegalAgreementLoadingContainer,
  LegalAgreementUserFieldTitle,
  LegalAgreementUserFields,
  LegalPolicyArticleContainer,
  LegalPolicyFields,
  LegalPolicyFormContainer,
  LegalPolicyHeading,
  LegalPolicyHeadingContainer,
  PolicyFormPageBackButton,
  PolicyPageNavHeader,
} from './components/styledComponents'
import { agreeTheAgreement, getClientIpAddress, getSingleLegalPolicy } from './legal_utils'
import './legalFormStyles.css'

export default function LegalForm({
  agreementId,
  afterSubmitFunction,
  showLayoutHeader = true,
  showPageHeader = true,
  singleLegalPolicyGetter = getSingleLegalPolicy,
  agreeTheAgreementPostFunction = agreeTheAgreement,
}) {
  const { id: paramId } = useParams()

  const id = agreementId ? agreementId : paramId
  const navigate = useNavigate()
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const {
    mentorProfileData,
    refreshLegalPolicies,
    allLegalPolicies,
    enteringFirstTime,
    endOnboardingFlow,
  } = useContext(MentorProfileContext)
  const [legalPolicy, setLegalPolicy] = useState()
  const [saving, setSaving] = useState(false)
  const [loadingPage, setLoadingPage] = useState(true)
  const [currentIpAddress, setCurrentIpAddress] = useState('')
  const [legalFormData, setLegalFormData] = useState({
    agreement: false,
  })
  const [locationObject, setLocationObject] = useState({})
  const [currentCountryCode, setCurrentCountryCode] = useState('in')
  const currentLocation = useMemo(
    () =>
      unSecureSetupData?.metaCountries?.find((country) => {
        return country?.id === currentCountryCode?.toLowerCase()
      }),
    [currentCountryCode],
  )
  const currentLegalPolicySummary = allLegalPolicies.find(
    (legalPolicy) => legalPolicy.agreementId === id,
  )

  const titleToShow = useMemo(() => {
    return getAllRolesInBoolean().isDoctor || mentorProfileData?.categories?.includes('doctor')
      ? legalPolicy?.title?.replace('Therapist', 'Doctor')
      : legalPolicy?.title
  }, [legalPolicy?.title, mentorProfileData])

  async function getLegalPolicy() {
    const legalPolicyResponse = await singleLegalPolicyGetter(id)
    if (legalPolicyResponse) setLegalPolicy(legalPolicyResponse)
  }

  async function getIPAddress() {
    const clientId = await getClientIpAddress()
    setCurrentIpAddress(clientId)
  }

  function handleChange(event) {
    setLegalFormData((prev) => ({
      ...prev,
      [event.target.id]:
        event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    }))
  }

  async function AgreePolicy() {
    const toSend = {
      ...legalPolicy,
      ipAddress: currentIpAddress,
      agreementId: id,
    }
    const response = await agreeTheAgreementPostFunction(toSend)
    if (response) {
      if (afterSubmitFunction) {
        await refreshLegalPolicies()
        // console.log({
        //   nonSignedAgreements:
        //     allLegalPolicies?.filter((agreement) => !agreement?.signed)?.length - 1,
        //   allAgreements: allLegalPolicies,
        //   enteringFirstTime: enteringFirstTime,
        // })
        allLegalPolicies?.filter((agreement) => !agreement?.signed)?.length - 1 >= 0 &&
          enteringFirstTime &&
          endOnboardingFlow()
        afterSubmitFunction()
      } else {
        navigate('/mentor-home/agreements/')
      }
      toast.success('Agreement Signed.')
    }
  }

  async function handleSubmit(event) {
    setSaving(true)
    const form = event.currentTarget
    if (form.checkValidity() === false) {
      event.preventDefault()
      event.stopPropagation()
    } else {
      event.preventDefault()
      await AgreePolicy()
    }
    setSaving(true)
  }

  function goBackToLegalPoliciesListing() {
    navigate('/mentor-home/agreements')
  }

  async function getGeoLocation() {
    const userLocation = await LocationService.getLocation()
    // const userCountryCode = await LocationService.getCountry(userLocation)
    const userCountryCode = getCountryCode()
    setLocationObject(
      userLocation || {
        lat: 12.9377677,
        lng: 77.6879585,
      },
    )
    setCurrentCountryCode(userCountryCode)
  }

  async function mapsSetup() {
    const google = window.google
    if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
      LocationService.initializeMaps()
      setTimeout(getGeoLocation, 500)
    } else {
      await getGeoLocation()
    }
  }

  async function Setup() {
    setLoadingPage(true)
    await Promise.all([mapsSetup(), getLegalPolicy(), getIPAddress()])
    setLoadingPage(false)
  }

  useEffect(() => {
    Setup()
  }, [])

  useEffect(() => {}, [locationObject])

  //TODO: ADD BACK BUTTON
  return (
    <>
      {showLayoutHeader && <MentorHeader heading="Agreements, Tax and Banking" />}
      <LegalPolicyFormContainer onSubmit={handleSubmit}>
        {showPageHeader && (
          <PolicyPageNavHeader>
            <PolicyFormPageBackButton
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={goBackToLegalPoliciesListing}
            >
              Back
            </PolicyFormPageBackButton>
          </PolicyPageNavHeader>
        )}
        {loadingPage ? (
          <LegalAgreementLoadingContainer>
            <CircularProgress sx={{ color: '#4f4f4f' }} />
          </LegalAgreementLoadingContainer>
        ) : (
          <>
            <LegalPolicyHeadingContainer>
              <LegalPolicyHeading>{titleToShow}</LegalPolicyHeading>
              <LegalPolicyFields>
                {legalPolicy?.createdTime && (
                  <HeadingFieldDiv>
                    <HeadingFields>Effective Date</HeadingFields>
                    <HeadingFields>
                      {currentLegalPolicySummary?.acceptedTime
                        ? dayjs(currentLegalPolicySummary?.acceptedTime).format(dateFormatHere)
                        : dayjs(new Date()).format(dateFormatHere)}
                    </HeadingFields>
                  </HeadingFieldDiv>
                )}
                <HeadingFieldDiv>
                  {currentLegalPolicySummary?.signed || currentLegalPolicySummary?.acceptedTime ? (
                    <>
                      <HeadingFields>Accepted On</HeadingFields>
                      <HeadingFields>
                        {dayjs(currentLegalPolicySummary?.acceptedTime).format(dateFormatHere)}
                      </HeadingFields>
                    </>
                  ) : (
                    <HeadingFields notAccepted>Not Accepted</HeadingFields>
                  )}
                </HeadingFieldDiv>
              </LegalPolicyFields>
            </LegalPolicyHeadingContainer>
            <LegalPolicyArticleContainer>
              {legalPolicy?.content ? parse(legalPolicy?.content) : ''}
              <LegalAgreementUserFields>
                <div>
                  <LegalAgreementUserFieldTitle>Legal Name: </LegalAgreementUserFieldTitle>
                  <span>{mentorProfileData?.name}</span>
                </div>
                <div>
                  <LegalAgreementUserFieldTitle>IP Address: </LegalAgreementUserFieldTitle>
                  <span>{currentIpAddress}</span>
                </div>
                <div>
                  <LegalAgreementUserFieldTitle>Location: </LegalAgreementUserFieldTitle>
                  <span>{currentLocation?.title || getCountryCode()}</span>
                </div>
                <div>
                  <LegalAgreementUserFieldTitle>Date: </LegalAgreementUserFieldTitle>
                  <span>
                    {currentLegalPolicySummary?.acceptedTime
                      ? dayjs(currentLegalPolicySummary?.acceptedTime).format(dateFormatHere)
                      : dayjs(new Date()).format(dateFormatHere)}
                  </span>
                </div>
              </LegalAgreementUserFields>
            </LegalPolicyArticleContainer>
            {!currentLegalPolicySummary?.signed && (
              <>
                <CustomCheckBox
                  id="agreement"
                  color="default"
                  required
                  label="I agree to the terms listed above"
                  makeWholeClickable
                  checked={legalFormData?.agreement || false}
                  onChange={handleChange}
                />
                <Box sx={{ m: 1, position: 'relative' }}>
                  <AgreeTermsButton disabled={!legalFormData?.agreement} type="submit">
                    Agree
                    {saving && (
                      <CircularProgress
                        color="secondary"
                        size={24}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          marginTop: '-12px',
                          marginLeft: '-12px',
                        }}
                      />
                    )}
                  </AgreeTermsButton>
                </Box>
              </>
            )}
          </>
        )}
      </LegalPolicyFormContainer>
    </>
  )
}

const dateFormatHere = 'MMMM D, YYYY, h:mm A'
